import { useNavigatableInitialData } from '@gik/core/hooks/hooks/useNavigatableInitialData';
import { NotFoundPage } from '@gik/core/pages/NotFound/NotFoundPage';
import { Debug } from '@gik/core/utils/asPlaceholderReactNode.tsx';
import { catchSSRError } from '@gik/core/utils/catchSSRError';
import { measureSSRPerformance } from '@gik/core/utils/measureSSRPerformance';
import { getTaxonomySlugParam } from '@gik/shop/components/ProductsGridSection';
import { GivePage } from '@gik/shop/pages/GivePage/GivePage';
import type { TaxonomyPageServerProps } from '@gik/shop/pages/TaxonomyPage/TaxonomyPage';
import { TaxonomyPage } from '@gik/shop/pages/TaxonomyPage/TaxonomyPage';
import type { GiveTaxonomyType } from '@gik/shop/types/GiveTaxonomyType';
import { getTaxonomies } from '@/api/give/taxonomies';
import { getPostsByTaxonomy } from '@/api/posts/postsByTaxonomy';
import { getPostsFeatured, giveHomeFeaturedArticlesParams } from '@/api/posts/postsFeatured';
import { getLatestPosts } from '@/api/posts/postsLatest';
import { getProductsBySlug } from '@/api/products/productsBySlug';
import type { PageLayoutProps } from '@/components/layout/PageLayout';
import { PageLayout } from '@/components/layout/PageLayout';
import { NotFoundError } from '@/utils/handleSSRError';
import type { GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';

export default function TaxonomyPageWrapper(props: TaxonomyPageServerProps): React.ReactElement {
  const router = useRouter();
  const slug = router.query?.slug;
  const pathname = typeof slug !== 'string' ? (slug as string[])?.join('/') || '' : slug;

  const taxonomyType = 'situation';
  const parentTaxonomy = slug ? slug[0] : undefined;
  const childTaxonomy = slug ? slug[1] : undefined;

  const fallbackData = useNavigatableInitialData(props);

  if ((!childTaxonomy && !parentTaxonomy) || (taxonomyType === 'situation' && parentTaxonomy && !childTaxonomy)) {
    // render the shop overview page when there is no taxonomy
    return (
      <>
        <Debug data={{ slug, pathname, taxonomyType, parentTaxonomy, childTaxonomy }} />
        <GivePage parentTaxonomy={parentTaxonomy} taxonomyType={taxonomyType} fallbackData={props} />
      </>
    );
  }

  // check if childTaxonomy resolves to an existing taxonomy
  function getTaxonomyTypeGroup() {
    switch (taxonomyType) {
      case 'situation':
        return 'situations';
    }
  }
  const taxonomyTypeGroup = getTaxonomyTypeGroup();

  const taxonomies = fallbackData ? fallbackData[taxonomyTypeGroup] : null;
  if (taxonomies && childTaxonomy) {
    const matchingTaxonomy = taxonomies.find(item => item.slug === childTaxonomy);
    if (!matchingTaxonomy) {
      return <NotFoundPage />;
    }
  }

  return (
    <>
      <Debug data={{ slug, pathname, taxonomyType, parentTaxonomy, childTaxonomy }} />
      <TaxonomyPage
        taxonomyType={taxonomyType}
        parentTaxonomy={parentTaxonomy}
        childTaxonomy={childTaxonomy}
        fallbackData={fallbackData}
        key={pathname}
      />
    </>
  );
}

TaxonomyPageWrapper.getLayout = function getLayout(page, layoutProps: PageLayoutProps) {
  return (
    <PageLayout {...layoutProps} key={layoutProps.key}>
      {page}
    </PageLayout>
  );
};

async function _getServerSideProps(context: GetServerSidePropsContext) {
  const path = context.resolvedUrl;

  const parts = path.split('?')[0].split('/');

  const taxonomyType = parts[3] as GiveTaxonomyType;
  const parentTaxonomy = parts[4];
  const childTaxonomy = parts[5];
  const slug = childTaxonomy || parentTaxonomy || taxonomyType;

  const [situations, recipients, categories] = await Promise.all([
    getTaxonomies('situation'),
    getTaxonomies('recipient'),
    getTaxonomies('category'),
  ]);

  const slugParam = getTaxonomySlugParam(taxonomyType);

  const props: TaxonomyPageServerProps = {
    situations,
    recipients,
    categories,
    products: null,
    productsTotalPages: null,
    posts: null,
    posts2: null,
  };

  if (slug && (parentTaxonomy || childTaxonomy)) {
    const productsResponse = await getProductsBySlug({
      [slugParam]: [slug],
      perPage: 12,
    }).response;
    props.products = JSON.parse(productsResponse.body);
    if (!props.products?.length) {
      throw new NotFoundError();
    }
    props.productsTotalPages = parseInt(productsResponse.headers['x-total-pages'] as string);
    if (taxonomyType === 'category') {
      // no relation between product categories and articles, so use latest articles
      props.posts = await getLatestPosts().json;
    } else {
      props.posts = await getPostsByTaxonomy({
        taxonomyItemSlug: taxonomyType,
        taxonomySlug: slug,
        page: 1,
        perPage: 4,
      });
      if (taxonomyType === 'situation') {
        props.posts2 = await getPostsByTaxonomy({
          taxonomyItemSlug: taxonomyType,
          taxonomySlug: slug,
          page: 2,
          perPage: 4,
        });
      }
    }
  } else {
    // use featured articles for the give home page
    props.posts = await getPostsFeatured(giveHomeFeaturedArticlesParams).json;
  }

  return {
    props,
  };
}

export const getServerSideProps = catchSSRError(measureSSRPerformance(_getServerSideProps), true);
